// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 28-it-dashboard-ui.md

/** Oracle clamps M2M certificates at two years; 730 days is the documented maximum. */
export const M2M_CERT_VALIDITY_DAYS = 730;

export interface GeneratedM2mCertificate {
  privateKeyPem: string;
  certificatePem: string;
}

const ECDSA_P256 = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const ECDSA_SHA256 = { name: 'ECDSA', hash: 'SHA-256' } as const;

function truncateToSeconds(date: Date): Date {
  const copy = new Date(date.getTime());
  copy.setMilliseconds(0);
  return copy;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function toPem(type: string, der: ArrayBuffer): string {
  const b64 = arrayBufferToBase64(der);
  const lines = b64.match(/.{1,64}/g) ?? [b64];
  return `-----BEGIN ${type}-----\n${lines.join('\n')}\n-----END ${type}-----\n`;
}

function randomSerialHex(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[0] = (bytes[0] ?? 0) & 0x7f;
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * ECDSA P-256 self-signed x.509 for `private_key_jwt` registration. The x509
 * library is loaded only when IT clicks Generate so the dashboard bundle stays
 * small. The private key never leaves the returned string.
 */
export async function generateM2mCertificate(): Promise<GeneratedM2mCertificate> {
  const x509 = await import('@peculiar/x509');
  x509.cryptoProvider.set(globalThis.crypto);

  const keys = await globalThis.crypto.subtle.generateKey(ECDSA_P256, true, ['sign', 'verify']);
  const notBefore = truncateToSeconds(new Date());
  const notAfter = truncateToSeconds(
    new Date(notBefore.getTime() + M2M_CERT_VALIDITY_DAYS * 24 * 60 * 60 * 1000),
  );

  const cert = await x509.X509CertificateGenerator.createSelfSigned({
    serialNumber: randomSerialHex(),
    name: 'CN=Greenlight M2M',
    notBefore,
    notAfter,
    signingAlgorithm: ECDSA_SHA256,
    keys,
    extensions: [
      new x509.BasicConstraintsExtension(false),
      new x509.KeyUsagesExtension(x509.KeyUsageFlags.digitalSignature, true),
    ],
  });

  const pkcs8 = await globalThis.crypto.subtle.exportKey('pkcs8', keys.privateKey);
  return {
    privateKeyPem: toPem('PRIVATE KEY', pkcs8),
    certificatePem: cert.toString('pem'),
  };
}
